/** Manual sensors (owner, 2026-10-22): sensors whose readings a person reads
 *  in the field and enters here, one at a time or pasted and imported from
 *  a spreadsheet, as Trimble 4D Control and Bentley VDV take field readings.
 *  `/sensors/manual` lists them; `/sensors/manual/:id` is one sensor: its
 *  chart, its readings, entry, import, export and properties. Kept on the
 *  server (db/034): a project's members see its manual sensors and enter
 *  readings, Viewers included; its Editors and Admins change the sensors
 *  (`can_edit`), and the server decides either way. */
import {
  useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState,
  type FormEvent, type KeyboardEvent, type ReactNode, type RefObject, type SyntheticEvent,
} from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../api/client'
import { SENSOR_TYPES, type Project, type Station } from '../api/types'
import { TimeSeriesChart } from '../components/TimeSeriesChart'
import { CSV_FORMAT_LABEL, csvValue, type CsvFormat } from '../lib/compare'
import {
  MAX_MANUAL_READINGS, MAX_READING_NOTE, MAX_SENSOR_NOTES,
  applyReadings, changeReading, deleteManualSensor, exportManualReadings, findReading, inputFor,
  inputsFromTimestamp, loadManualReadings, manualChartSeries, manualExportFileName, manualReadingNotes,
  manualReadingsTemplate, manualSensorStore, manualSourceId, parseReadingValue, parseReadingsText, planImport,
  planReading, readingFocusAfter, readingTimeLabel, removeReading, saveManualSensor, timestampFromInputs,
  toStationIso, validateManualSensorInput,
  type ImportPlan, type ManualReading, type ManualSensor, type ManualSensorInput, type ManualSensorReadings,
  type ReadingFocus,
} from '../lib/manualSensors'
import { useRemote } from '../lib/remoteStore'
import { formatDateTime } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import './manual-sensors.css'

const SHARED_NOTE = 'Readings are kept on the platform for everyone in the project; each records who entered it.'
const failure = (cause: unknown, fallback: string) => cause instanceof Error ? cause.message : fallback
const PARAMETERS = ['Water level', 'Water depth', 'Discharge', 'Piezometric level', 'Crack width', 'Displacement',
  'Settlement', 'Tilt', 'Temperature', 'Precipitation', 'Turbidity']
const UNITS = ['m', 'cm', 'mm', 'm³/s', 'l/s', 'kPa', '°C', '°', 'mm/day', 'NTU']

/** The stored value at the sensor's precision, never losing a digit: the
 *  rule the platform's exports follow, so the table and a file agree. */
function valueText(value: number, precision: number): string { return csvValue(value, precision) }
function unitText(unit: string): string { return unit || 'Unitless' }
function count(n: number, one: string, many = one + 's'): string { return n.toLocaleString('en-GB') + ' ' + (n === 1 ? one : many) }

type FocusAfter = (find: () => HTMLElement | null | undefined) => void

/** Keyboard focus moved once React has drawn what a change brings, not in
 *  the click that caused it: the element to focus (the edit form's first
 *  field, a row's Edit button back in place of its form) does not exist
 *  until then, and the button that had the focus may be gone already. */
function useFocusAfterRender(): FocusAfter {
  const [request, setRequest] = useState<{ find: () => HTMLElement | null | undefined } | null>(null)
  useEffect(() => { request?.find()?.focus() }, [request])
  return useCallback(find => setRequest({ find }), [])
}

/** Escape as "never mind" inside an inline question or edit row. Not while
 *  an input method is composing, where Escape abandons the composition. */
function onEscape(action: () => void) {
  return (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return
    event.preventDefault()
    // An inline question inside an edit row closes alone, not the row too.
    event.stopPropagation()
    action()
  }
}

/** What had the focus inside `form` when it asked a question, for putting
 *  it back if the reader declines; Safari does not focus a clicked button,
 *  so it may be nothing in the form at all. */
function focusedIn(form: HTMLElement | null): HTMLElement | null {
  const active = document.activeElement
  return form && active instanceof HTMLElement && form.contains(active) ? active : null
}

/** One sensor's stored readings, loaded with its page and again after
 *  every write; `reload` answers with the new list, so the focus can be
 *  placed on the row a write left behind. */
function useReadings(id: string) {
  const [readings, setReadings] = useState<ManualReading[] | null>(null)
  const [error, setError] = useState('')
  const reload = useCallback(async (): Promise<ManualReading[]> => {
    try {
      const loaded = await loadManualReadings(id)
      setReadings(loaded); setError('')
      return loaded
    } catch (cause) {
      setError(failure(cause, 'The readings could not be loaded.'))
      return []
    }
  }, [id])
  useEffect(() => { void reload() }, [reload])
  return { readings, error, reload }
}

type SensorDialogState = { mode: 'create' } | { mode: 'edit'; sensor: ManualSensor }

export function ManualSensors() {
  const { id } = useParams()
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const store = useRemote(manualSensorStore)
  const [loadError, setLoadError] = useState('')
  useEffect(() => { setLoadError(store.error) }, [store.error])
  const [notice, setNotice] = useState('')
  const [dialog, setDialog] = useState<SensorDialogState | null>(null)
  // The sensor just deleted, until the list has replaced it: without this
  // the page reads "not found" for the moment between the two.
  const [deleted, setDeleted] = useState<string | null>(null)
  const stations = useAsync(() => api.stations(), [])
  // The projects a new sensor can go in: those the reader is an Editor or
  // Admin of (every one for a platform admin).
  const projects = useAsync(() => api.projects.list().then(data => data.projects), [])
  const stationNames = useMemo(() => new Map((stations.data ?? []).map(station => [station.code, station.name])), [stations.data])

  useEffect(() => {
    if (params.get('new') !== '1') return
    setDialog({ mode: 'create' })
    const next = new URLSearchParams(params)
    next.delete('new')
    setParams(next, { replace: true })
  }, [params, setParams])
  // A notice belongs to the sensor it was about; a carried one (deleting a
  // sensor returns to the list saying what was deleted) is shown once and
  // dropped from history, so a reload does not announce it again.
  useEffect(() => { setNotice(''); setDeleted(null) }, [id])
  useEffect(() => {
    const carried = (location.state as { notice?: unknown } | null)?.notice
    if (typeof carried !== 'string') return
    setNotice(carried)
    navigate(location.pathname + location.search, { replace: true, state: null })
  }, [location.state, location.pathname, location.search, navigate])

  const sensor = id ? store.items.find(item => item.id === id) : undefined
  const messages = <>
    {loadError && <Notice onDismiss={() => setLoadError('')}>{loadError}</Notice>}
    {notice && <Notice onDismiss={() => setNotice('')}>{notice}</Notice>}
  </>

  return <section className="mns-page" aria-labelledby="manual-sensors-title">
    {id !== undefined && id === deleted && !sensor ? null : id === undefined
      ? <SensorList items={store.items} loaded={store.loaded} stationNames={stationNames} messages={messages}
          canCreate={(projects.data ?? []).some(project => project.my_role === 'editor' || project.my_role === 'admin')}
          onCreate={() => setDialog({ mode: 'create' })} />
      : sensor
        ? <SensorDetail key={sensor.id} sensor={sensor} stationNames={stationNames} messages={messages}
            onNotice={setNotice} onEdit={() => setDialog({ mode: 'edit', sensor })}
            onDelete={async () => {
              setDeleted(sensor.id)
              try { await deleteManualSensor(sensor.id) } catch (cause) { setDeleted(null); return failure(cause, 'Could not delete this sensor.') }
              navigate('/sensors/manual', { state: { notice: sensor.name + ' deleted.' } })
              return null
            }} />
        : !store.loaded ? <p className="mns-muted" role="status">Loading manual sensors…</p>
        : <div className="mns-missing">
            <h1 id="manual-sensors-title">Manual Sensor Not Found</h1>
            <p>This manual sensor does not exist, or it is in a project you are not in.</p>
            <Link className="button" to="/sensors/manual">All manual sensors</Link>
          </div>}
    {dialog && <SensorDialog state={dialog} items={store.items} stations={stations.data ?? []} stationsError={stations.error}
      projects={(projects.data ?? []).filter(project => project.my_role === 'editor' || project.my_role === 'admin')}
      projectsError={projects.error} onClose={() => setDialog(null)}
      onSubmit={async (input) => {
        try {
          const saved = await saveManualSensor(input, dialog.mode === 'edit' ? dialog.sensor.id : undefined)
          setDialog(null)
          if (dialog.mode === 'create') navigate('/sensors/manual/' + encodeURIComponent(saved.id))
          else setNotice('Properties saved.')
          return null
        } catch (cause) { return failure(cause, 'Check these properties.') }
      }} />}
  </section>
}

function Notice({ children, onDismiss }: { children: ReactNode; onDismiss: () => void }) {
  return <div className="mns-notice" role="status">{children}
    <button type="button" aria-label="Dismiss notice" onClick={onDismiss}>×</button></div>
}

// ------------------------------------------------------------------- list

function SensorList({ items, loaded, stationNames, messages, canCreate, onCreate }: {
  items: ManualSensor[]; loaded: boolean; stationNames: Map<string, string>; messages: ReactNode
  canCreate: boolean; onCreate: () => void
}) {
  const [query, setQuery] = useState('')
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  const matching = items.filter(item => {
    const text = [item.name, item.sensor_type, item.quantity, item.unit, item.station ?? '',
      item.station_name ?? '', item.project].join(' ').toLocaleLowerCase()
    return terms.every(term => text.includes(term))
  })
  return <>
    <header className="mns-heading">
      <div><h1 id="manual-sensors-title" className="sr-only">Manual Sensors</h1>
        <p className="mns-lead">Sensors read by hand in the field: staff gauges, dipmeters, crack gauges. Enter readings one at a time or paste them from a spreadsheet.</p></div>
      {canCreate && <button type="button" className="button primary" onClick={onCreate}>New manual sensor</button>}
    </header>
    {messages}
    <section className="mns-card" aria-labelledby="mns-list-title">
      <div className="mns-card-head">
        <h2 id="mns-list-title">Sensors <span className="mns-count">{items.length}</span></h2>
        <input type="search" className="mns-search" aria-label="Search manual sensors" placeholder="Search name, station, parameter or project"
          value={query} onChange={event => setQuery(event.target.value)} />
      </div>
      {matching.length ? <div className="mns-table-wrap"><table className="mns-table">
        <thead><tr><th scope="col">Name</th><th scope="col">Type</th><th scope="col">Station</th>
          <th scope="col" className="mns-num">Readings</th><th scope="col">Latest reading</th><th scope="col" className="mns-num">Latest value</th></tr></thead>
        <tbody>{matching.map(item => {
          const latest = item.latest
          return <tr key={item.id}>
            <th scope="row"><Link to={'/sensors/manual/' + encodeURIComponent(item.id)}>{item.name}</Link>
              <small>{item.quantity} · {item.project}</small></th>
            <td>{item.sensor_type}</td>
            <td>{item.station ? stationNames.get(item.station) ?? item.station_name ?? <span className="mono">{item.station}</span> : <span className="mns-muted">None</span>}</td>
            <td className="mns-num mono">{item.reading_count.toLocaleString('en-GB')}</td>
            <td className="mono">{latest ? readingTimeLabel(latest.ts) : <span className="mns-muted">No readings yet</span>}</td>
            <td className="mns-num mono">{latest ? valueText(latest.value, item.precision) + (item.unit ? ' ' + item.unit : '') : ''}</td>
          </tr>
        })}</tbody>
      </table></div>
        : <div className="mns-empty">{!loaded ? <p role="status">Loading manual sensors…</p> : items.length
          ? <p>No manual sensors match this search.</p>
          : <><strong>No manual sensors yet</strong><p>{canCreate
              ? 'Create one for each gauge read by hand, then enter or import its readings.'
              : "A project's Editors and Admins create manual sensors; once one is in a project of yours, you can enter its readings."}</p>
            {canCreate && <button type="button" className="button" onClick={onCreate}>New manual sensor</button>}</>}</div>}
      <p className="mns-card-foot">Times are station time, Asia/Tbilisi (UTC+4). {SHARED_NOTE}</p>
    </section>
  </>
}

// ----------------------------------------------------------------- detail

function SensorDetail({ sensor: described, stationNames, messages, onNotice, onEdit, onDelete }: {
  sensor: ManualSensor; stationNames: Map<string, string>; messages: ReactNode
  onNotice: (text: string) => void; onEdit: () => void; onDelete: () => Promise<string | null>
}) {
  const loaded = useReadings(described.id)
  // The page draws the sensor with its readings once they have come; until
  // then the chart and table wait, rather than say "No readings yet".
  const sensor: ManualSensorReadings = useMemo(() => ({ ...described, readings: loaded.readings ?? [] }),
    [described, loaded.readings])
  const [importOpen, setImportOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [format, setFormat] = useState<CsvFormat>('csv-excel')
  // Held here rather than in the table: deleting the last reading takes the
  // table away, and the focus then goes to the chart's heading above it.
  const focusAfter = useFocusAfterRender()
  const chartHeading = useRef<HTMLHeadingElement>(null)
  // Held while the sensor is unchanged: the chart keeps its option, and the
  // reader's zoom, only while its series hold the same points arrays.
  const chartSeries = useMemo(() => [manualChartSeries(sensor)], [sensor])
  const notes = useMemo(() => manualReadingNotes(sensor), [sensor])
  const station = sensor.station ? stationNames.get(sensor.station) ?? sensor.station_name ?? sensor.station : null
  const latest = sensor.readings[sensor.readings.length - 1]
  const download = (text: string, name: string) => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = name
    link.click()
    URL.revokeObjectURL(url)
  }

  return <>
    <nav className="mns-crumb" aria-label="Breadcrumb"><Link to="/sensors/manual">Manual Sensors</Link><span aria-hidden="true">/</span><span>{sensor.name}</span></nav>
    <header className="mns-heading">
      <div><div className="mns-eyebrow">Manual sensor</div><h1 id="manual-sensors-title">{sensor.name}</h1>
        <p className="mns-lead">{[sensor.sensor_type, sensor.quantity !== sensor.sensor_type ? sensor.quantity : '', unitText(sensor.unit), station ?? 'No station', sensor.project]
          .filter(Boolean).join(' · ')}</p></div>
      <div className="mns-heading-actions">
        <Link className="button" to={'/analysis/new?s=' + encodeURIComponent(manualSourceId(sensor.id))}>Use in analysis</Link>
        {sensor.can_edit && <button type="button" className="button" onClick={onEdit}>Edit properties</button>}
        {sensor.can_edit && <button type="button" className="button mns-danger" onClick={() => setConfirmDelete(true)}>Delete sensor</button>}
      </div>
    </header>
    {messages}
    {loaded.error && <p className="mns-error" role="alert">{loaded.error}</p>}
    <div className="mns-detail">
      <div className="mns-main">
        <section className="mns-card" aria-labelledby="mns-chart-title">
          <div className="mns-card-head">
            <div><h2 id="mns-chart-title" ref={chartHeading} tabIndex={-1}>Readings</h2>
              <span className="mns-card-sub">{count(sensor.readings.length, 'reading')} · Tbilisi time (UTC+4)</span></div>
            {latest && <div className="mns-latest"><span>Latest</span>
              <strong className="mono">{valueText(latest.value, sensor.precision)} <small>{sensor.unit}</small></strong>
              <span className="mono">{readingTimeLabel(latest.ts)}</span></div>}
          </div>
          {/* The platform's time-series chart: its station-time axis and
              tooltip, its gap breaks (so a missed month reads as no data),
              its zoom and Reset zoom. With markers, since a field reading is
              weekly or monthly and a lone reading between two gaps would
              otherwise draw nothing; each reading's note under its value,
              and its time as the table writes it, seconds included. */}
          {loaded.readings === null
            ? <div className="mns-empty mns-chart-empty"><p role="status">Loading readings…</p></div>
            : sensor.readings.length
            ? <div className="mns-chart"><TimeSeriesChart series={chartSeries} height={340} markers
                pointNote={(_, at) => notes.get(at)} pointTime={readingTimeLabel} /></div>
            : <div className="mns-empty mns-chart-empty"><strong>No readings yet</strong>
              <p>Add a reading, or paste readings from a spreadsheet.</p>
              {sensor.can_enter && <button type="button" className="button" onClick={() => setImportOpen(true)}>Paste or import readings</button>}</div>}
        </section>
        {sensor.readings.length > 0 && <ReadingsTable sensor={sensor} reload={loaded.reload} onNotice={onNotice}
          focusAfter={focusAfter} chartHeading={chartHeading} />}
      </div>
      <aside className="mns-side" aria-label="Enter and export readings">
        {sensor.can_enter && loaded.readings !== null && <AddReading sensor={sensor} reload={loaded.reload} onNotice={onNotice}
          onImport={() => setImportOpen(true)} />}
        <section className="mns-card mns-pad" aria-labelledby="mns-export-title">
          <h2 id="mns-export-title">Export</h2>
          <p className="mns-help">The readings as a CSV file: time, value and note, in the platform's two dialects. Edit it in Excel and import it back here.</p>
          <label className="mns-field"><span>Format</span>
            <select value={format} onChange={event => setFormat(event.target.value as CsvFormat)}>
              {(Object.keys(CSV_FORMAT_LABEL) as CsvFormat[]).map(item => <option key={item} value={item}>{CSV_FORMAT_LABEL[item]}</option>)}
            </select></label>
          <div className="mns-row-actions">
            <button type="button" className="button" disabled={!sensor.readings.length}
              onClick={() => download(exportManualReadings(sensor, format), manualExportFileName(sensor))}>Export readings</button>
            <button type="button" className="button" onClick={() => download(manualReadingsTemplate(sensor, format), manualExportFileName({ ...sensor, readings: [] }, 'template'))}>Download template</button>
          </div>
        </section>
        <section className="mns-card mns-pad" aria-labelledby="mns-properties-title">
          <div className="mns-card-row"><h2 id="mns-properties-title">Properties</h2>
            {sensor.can_edit && <button type="button" className="mns-text-button" onClick={onEdit}>Edit</button>}</div>
          <dl className="mns-properties">
            <dt>Sensor type</dt><dd>{sensor.sensor_type}</dd>
            <dt>Measures</dt><dd>{sensor.quantity}</dd>
            <dt>Unit</dt><dd>{unitText(sensor.unit)}</dd>
            <dt>Decimal places</dt><dd className="mono">{sensor.precision}</dd>
            <dt>Station</dt><dd>{station ?? 'None'}{sensor.station && station !== sensor.station && <small className="mono"> {sensor.station}</small>}</dd>
            <dt>Project</dt><dd>{sensor.project}</dd>
            <dt>Source ID</dt><dd className="mono">{manualSourceId(sensor.id)}</dd>
            <dt>Created</dt><dd className="mono">{formatDateTime(sensor.created_at)}</dd>
            <dt>Updated</dt><dd className="mono">{formatDateTime(sensor.updated_at)}</dd>
          </dl>
          {sensor.notes && <p className="mns-notes">{sensor.notes}</p>}
        </section>
      </aside>
    </div>
    {/* The chart's heading, when the import came from the chart's empty
        state and the chart now stands where its button was. */}
    {importOpen && <ImportDialog sensor={sensor} fallback={chartHeading} onClose={() => setImportOpen(false)} onApply={async (plan, name) => {
      try {
        const done = await applyReadings(sensor.id, plan, 'import', name)
        await loaded.reload()
        setImportOpen(false)
        onNotice([done.added ? count(done.added, 'reading') + ' added' : '', done.replaced ? count(done.replaced, 'reading') + ' replaced' : '']
          .filter(Boolean).join(', ') + '.')
        return null
      } catch (cause) { return failure(cause, 'Could not apply these readings.') }
    }} />}
    {confirmDelete && <DeleteSensorDialog sensor={sensor} onCancel={() => setConfirmDelete(false)} onDelete={onDelete} />}
  </>
}

// --------------------------------------------------------------- entry

/** "Replace it?" for a time that already holds a reading: re-entering a
 *  time replaces the stored reading, and only after the reader says so.
 *  A group in the form's own flow, not an alertdialog, which promises a
 *  modal this is not. It takes the place of the button that was pressed, so
 *  the focus moves to Replace as it appears, and the question is that
 *  button's description, read out with it. Escape keeps the stored one. */
function ReplaceConfirm({ previous, value, unit, precision, onReplace, onKeep }: {
  previous: ManualReading; value: number; unit: string; precision: number; onReplace: () => void; onKeep: () => void
}) {
  const replace = useRef<HTMLButtonElement>(null)
  const question = useId()
  useEffect(() => { replace.current?.focus() }, [])
  return <div className="mns-confirm" role="group" aria-label="Replace stored reading" onKeyDown={onEscape(onKeep)}>
    <p id={question}>A reading at <span className="mono">{readingTimeLabel(previous.ts)}</span> is already stored:{' '}
      <strong className="mono">{valueText(previous.value, precision)} {unit}</strong>{previous.note ? ` (${previous.note})` : ''}.
      Replace it with <strong className="mono">{valueText(value, precision)} {unit}</strong>?</p>
    <div className="mns-row-actions">
      <button ref={replace} type="button" className="button primary" aria-describedby={question} onClick={onReplace}>Replace reading</button>
      <button type="button" className="button" onClick={onKeep}>Keep stored reading</button></div>
  </div>
}

function AddReading({ sensor, reload, onNotice, onImport }: {
  sensor: ManualSensorReadings; reload: () => Promise<ManualReading[]>; onNotice: (text: string) => void; onImport: () => void
}) {
  // Now, to the minute, in station time: the reading most often typed is
  // the one just taken.
  const [when, setWhen] = useState(() => inputsFromTimestamp(toStationIso(new Date(Math.floor(Date.now() / 60_000) * 60_000))))
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState<{ plan: ImportPlan; value: number } | null>(null)
  const form = useRef<HTMLFormElement>(null)
  const valueField = useRef<HTMLInputElement>(null)
  const addButton = useRef<HTMLButtonElement>(null)
  // What had the focus when the form asked to replace a reading.
  const asking = useRef<HTMLElement | null>(null)
  const focusAfter = useFocusAfterRender()
  const change = (apply: () => void) => { apply(); setError(''); setPending(null) }

  const [busy, setBusy] = useState(false)
  const apply = async (plan: ImportPlan) => {
    setBusy(true)
    try {
      await applyReadings(sensor.id, plan, 'typed')
      await reload()
    } catch (cause) {
      setPending(null); setError(failure(cause, 'Could not save this reading.'))
      return
    } finally { setBusy(false) }
    setPending(null); setValue(''); setNote(''); setError('')
    onNotice(plan.replace ? 'Reading replaced.' : 'Reading added.')
    valueField.current?.focus()
  }
  // Back where the reader was: the field Enter was pressed in, or Add
  // reading, which the question had hidden and which is drawn again.
  const keep = () => {
    setPending(null)
    const from = asking.current
    focusAfter(() => from?.isConnected ? from : addButton.current)
  }
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const ts = timestampFromInputs(when.date, when.time)
    if (!ts) { setError('Enter a valid date and time.'); return }
    const parsed = parseReadingValue(value)
    if (typeof parsed === 'string') { setError(parsed); return }
    const plan = planReading(sensor, { ts, value: parsed, note }, Date.now())
    if (plan.errors.length) { setError(plan.errors[0]!.message); return }
    if (plan.blocked) { setError(plan.blocked); return }
    const row = plan.rows[0]
    if (row?.status === 'unchanged') { setError('This reading is already stored.'); return }
    if (row?.status === 'replace') { asking.current = focusedIn(form.current); setPending({ plan, value: parsed }); return }
    void apply(plan)
  }

  return <section className="mns-card mns-pad" aria-labelledby="mns-add-title">
    <h2 id="mns-add-title">Add Reading</h2>
    <form ref={form} className="mns-add" onSubmit={submit} noValidate>
      <div className="mns-field-row">
        <label className="mns-field"><span>Date</span>
          <input type="date" required value={when.date} onChange={event => { const date = event.target.value; change(() => setWhen(w => ({ ...w, date }))) }} /></label>
        <label className="mns-field"><span>Time</span>
          <input type="time" required step={60} value={when.time} onChange={event => { const time = event.target.value; change(() => setWhen(w => ({ ...w, time }))) }} /></label>
      </div>
      <label className="mns-field"><span>Value{sensor.unit ? ` (${sensor.unit})` : ''}</span>
        <input ref={valueField} className="mono" inputMode="decimal" autoComplete="off" required value={value}
          placeholder={(0).toFixed(sensor.precision).replace('.', ',')} onChange={event => { const next = event.target.value; change(() => setValue(next)) }} /></label>
      <label className="mns-field"><span>Note <small>Optional</small></span>
        <input maxLength={MAX_READING_NOTE} value={note} placeholder="Conditions, who read it" onChange={event => { const next = event.target.value; change(() => setNote(next)) }} /></label>
      <p className="mns-help">Station time, Asia/Tbilisi (UTC+4). A decimal comma or point both work.</p>
      {error && <p className="mns-error" role="alert">{error}</p>}
      {pending && pending.plan.rows[0]?.previous && <ReplaceConfirm previous={pending.plan.rows[0].previous} value={pending.value}
        unit={sensor.unit} precision={sensor.precision} onReplace={() => void apply(pending.plan)} onKeep={keep} />}
      {!pending && <div className="mns-row-actions">
        <button ref={addButton} type="submit" className="button primary" disabled={busy}>Add reading</button>
        <button type="button" className="button" onClick={onImport}>Paste or import</button>
      </div>}
    </form>
  </section>
}

interface RowEdit { original: string; date: string; time: string; value: string; note: string; error: string; confirm?: ManualReading }
const PAGE = 100

function ReadingsTable({ sensor, reload, onNotice, focusAfter, chartHeading }: {
  sensor: ManualSensorReadings; reload: () => Promise<ManualReading[]>; onNotice: (text: string) => void
  focusAfter: FocusAfter; chartHeading: RefObject<HTMLElement>
}) {
  const [shown, setShown] = useState(PAGE)
  const [editing, setEditing] = useState<RowEdit | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [error, setError] = useState('')
  const newest = useMemo(() => [...sensor.readings].reverse(), [sensor.readings])
  const heading = useRef<HTMLHeadingElement>(null)
  const editForm = useRef<HTMLFormElement>(null)
  const dateField = useRef<HTMLInputElement>(null)
  const saveButton = useRef<HTMLButtonElement>(null)
  const keepButton = useRef<HTMLButtonElement>(null)
  // Each listed row's Edit button by its reading's time, to hand the focus
  // back to the row a form or a question was about.
  const editButtons = useRef(new Map<string, HTMLButtonElement>())
  // What had the focus when Save asked to replace another reading.
  const asking = useRef<HTMLElement | null>(null)

  const focusOn = (target: ReadingFocus) => focusAfter(() => target.kind === 'row' ? editButtons.current.get(target.ts)
    : target.kind === 'table' ? heading.current : chartHeading.current)
  const startEdit = (reading: ManualReading) => {
    setDeleting(null); setError('')
    setEditing({ original: reading.ts, ...inputsFromTimestamp(reading.ts), value: String(reading.value), note: reading.note ?? '', error: '' })
    focusAfter(() => dateField.current)
  }
  const cancelEdit = () => {
    if (!editing) return
    setEditing(null)
    focusOn(readingFocusAfter(sensor, editing.original, shown))
  }
  const saveEdit = async (confirmed = false) => {
    if (!editing) return
    // Replace has the focus while the question is up, and the question
    // closes on an error: the focus goes to Save, drawn again in its place.
    const fail = (message: string) => {
      setEditing({ ...editing, error: message, confirm: undefined })
      if (editing.confirm) focusAfter(() => saveButton.current)
    }
    const ts = timestampFromInputs(editing.date, editing.time)
    if (!ts) { fail('Enter a valid date and time.'); return }
    const value = parseReadingValue(editing.value)
    if (typeof value === 'string') { fail(value); return }
    const other = ts !== editing.original ? findReading(sensor, ts) : undefined
    if (other && !confirmed) { asking.current = focusedIn(editForm.current); setEditing({ ...editing, error: '', confirm: other }); return }
    const reading = findReading(sensor, editing.original)
    if (!reading) { fail('This reading is no longer stored.'); return }
    try {
      const changed = await changeReading(reading, { ts, value, note: editing.note }, confirmed)
      const next = changed ? await reload() : sensor.readings
      setEditing(null)
      if (changed) onNotice(other ? 'Reading moved, replacing the one stored at that time.' : 'Reading saved.')
      focusOn(readingFocusAfter({ readings: next }, ts, shown))
    } catch (cause) { fail(failure(cause, 'Check this reading.')) }
  }
  // The edit stays open with what was typed; the focus goes back where it
  // was, or to Save, which the question had hidden.
  const keepOther = () => {
    if (!editing) return
    setEditing({ ...editing, confirm: undefined })
    const from = asking.current
    focusAfter(() => from?.isConnected ? from : saveButton.current)
  }
  const askDelete = (ts: string) => {
    setEditing(null); setDeleting(ts)
    // Keep, not Delete: Enter pressed twice must not delete a reading.
    focusAfter(() => keepButton.current)
  }
  const keepReading = () => {
    if (deleting === null) return
    setDeleting(null)
    focusOn(readingFocusAfter(sensor, deleting, shown))
  }
  const remove = async (ts: string) => {
    const reading = findReading(sensor, ts)
    if (!reading) return
    let next: ManualReading[]
    try {
      await removeReading(reading)
      next = await reload()
    } catch (cause) { setError(failure(cause, 'Could not delete this reading.')); return }
    setDeleting(null); setError('')
    onNotice('Reading deleted.')
    focusOn(readingFocusAfter({ readings: next }, null, shown))
  }
  // To the first of the rows just listed, where reading carries on; Show
  // more itself goes once the last of them is listed.
  const showMore = () => {
    const first = newest[shown]
    setShown(n => n + PAGE * 2)
    if (first) focusOn({ kind: 'row', ts: first.ts })
  }

  return <section className="mns-card" aria-labelledby="mns-table-title">
    <div className="mns-card-head"><div><h2 id="mns-table-title" ref={heading} tabIndex={-1}>Readings Table</h2>
      <span className="mns-card-sub">Newest first · up to {MAX_MANUAL_READINGS.toLocaleString('en-GB')} readings per sensor</span></div></div>
    {error && <p className="mns-error mns-inset" role="alert">{error}</p>}
    {newest.length ? <div className="mns-table-wrap"><table className="mns-table mns-readings">
      <thead><tr><th scope="col">Time (Tbilisi)</th><th scope="col" className="mns-num">Value{sensor.unit ? ` (${sensor.unit})` : ''}</th>
        <th scope="col">Note</th><th scope="col">Entered</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
      <tbody>{newest.slice(0, shown).map(reading => editing?.original === reading.ts
        ? <tr key={reading.ts} className="mns-editing"><td colSpan={5} onKeyDown={onEscape(cancelEdit)}>
            <form ref={editForm} className="mns-edit" aria-label={'Edit reading at ' + readingTimeLabel(reading.ts)}
              onSubmit={event => { event.preventDefault(); void saveEdit() }} noValidate>
              <label className="mns-field"><span>Date</span><input ref={dateField} type="date" value={editing.date} onChange={event => setEditing({ ...editing, date: event.target.value, error: '', confirm: undefined })} /></label>
              <label className="mns-field"><span>Time</span><input type="time" step={editing.time.length > 5 ? 1 : 60} value={editing.time} onChange={event => setEditing({ ...editing, time: event.target.value, error: '', confirm: undefined })} /></label>
              <label className="mns-field"><span>Value</span><input className="mono" inputMode="decimal" value={editing.value} onChange={event => setEditing({ ...editing, value: event.target.value, error: '', confirm: undefined })} /></label>
              <label className="mns-field mns-grow"><span>Note</span><input maxLength={MAX_READING_NOTE} value={editing.note} onChange={event => setEditing({ ...editing, note: event.target.value, error: '', confirm: undefined })} /></label>
              {!editing.confirm && <div className="mns-row-actions"><button ref={saveButton} type="submit" className="button primary">Save</button>
                <button type="button" className="button" onClick={cancelEdit}>Cancel</button></div>}
            </form>
            {editing.error && <p className="mns-error" role="alert">{editing.error}</p>}
            {editing.confirm && <ReplaceConfirm previous={editing.confirm} value={Number(parseReadingValue(editing.value))} unit={sensor.unit}
              precision={sensor.precision} onReplace={() => void saveEdit(true)} onKeep={keepOther} />}
          </td></tr>
        : <tr key={reading.ts}>
            <td className="mono">{readingTimeLabel(reading.ts)}</td>
            <td className="mns-num mono" title={String(reading.value)}>{valueText(reading.value, sensor.precision)}</td>
            <td className="mns-note">{reading.note ?? ''}</td>
            <td className="mono mns-muted" title={'Entered by ' + reading.entered_by}>{formatDateTime(reading.entered_at)}
              <small className="mns-by">{reading.entered_by}</small></td>
            <td className="mns-actions">{deleting === reading.ts
              ? <span className="mns-inline-confirm" role="group" aria-label={'Delete reading at ' + readingTimeLabel(reading.ts) + '?'}
                  onKeyDown={onEscape(keepReading)}>Delete this reading?
                <button type="button" className="mns-text-button mns-danger" onClick={() => void remove(reading.ts)}>Delete</button>
                <button ref={keepButton} type="button" className="mns-text-button" onClick={keepReading}>Keep</button></span>
              : <><button type="button" className="mns-text-button" aria-label={'Edit reading at ' + readingTimeLabel(reading.ts)}
                  ref={button => { if (button) editButtons.current.set(reading.ts, button); else editButtons.current.delete(reading.ts) }}
                  onClick={() => startEdit(reading)}>Edit</button>
                <button type="button" className="mns-text-button" aria-label={'Delete reading at ' + readingTimeLabel(reading.ts)} onClick={() => askDelete(reading.ts)}>Delete</button></>}</td>
          </tr>)}</tbody>
    </table></div> : <div className="mns-empty"><p>No readings yet.</p></div>}
    {newest.length > shown && <div className="mns-more"><span>Showing {shown.toLocaleString('en-GB')} of {newest.length.toLocaleString('en-GB')}</span>
      <button type="button" className="button" onClick={showMore}>Show more</button></div>}
  </section>
}

// ---------------------------------------------------------------- dialogs

/** A modal dialog that returns focus where it came from. `first` takes the
 *  focus on opening, rather than the close button the browser would pick.
 *  `fallback` takes it on closing when what opened the dialog is gone, or
 *  was never known: the chart's empty state opens the import, and applying
 *  it draws the chart in the button's place, so focusing that button would
 *  do nothing and the focus would fall to <body> with the dialog. */
function useModal(onClose: () => void, first?: RefObject<HTMLElement>, fallback?: RefObject<HTMLElement>) {
  const dialog = useRef<HTMLDialogElement>(null)
  // What opened the dialog, kept from the effect's first run. StrictMode
  // runs it twice in development, the second time with the dialog open
  // and the focus already inside it, and that field, gone once the dialog
  // closes, was being kept as the opener. <body> is no opener either:
  // Safari does not focus a clicked button.
  const opener = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const active = document.activeElement
    if (active instanceof HTMLElement && active !== document.body && !dialog.current?.contains(active)) opener.current = active
    dialog.current?.showModal()
    first?.current?.focus()
    return () => {
      const focus = opener.current
      if (focus?.isConnected) focus.focus(); else fallback?.current?.focus()
    }
    // Once, on opening: `first` and `fallback` are refs and stay the same
    // objects; `fallback.current` is read on closing, as it is then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { ref: dialog, onCancel: (event: SyntheticEvent) => { event.preventDefault(); onClose() } }
}

const MAX_FILE_BYTES = 2_000_000
/** The most characters the server accepts for what a sensor measures. */
const MAX_QUANTITY = 80

function ImportDialog({ sensor, onClose, onApply, fallback }: {
  sensor: ManualSensorReadings; onClose: () => void
  onApply: (plan: ImportPlan, importName: string | null) => Promise<string | null>; fallback: RefObject<HTMLElement>
}) {
  const field = useRef<HTMLTextAreaElement>(null)
  const modal = useModal(onClose, field, fallback)
  const [text, setText] = useState('')
  const [fileNote, setFileNote] = useState('')
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const deferred = useDeferredValue(text)
  const parsed = useMemo(() => deferred.trim() ? parseReadingsText(deferred) : null, [deferred])
  const plan = useMemo(() => parsed ? planImport(sensor, parsed, Date.now()) : null, [parsed, sensor])
  const changes = plan ? plan.add + plan.replace : 0
  useEffect(() => { setConfirmReplace(false); setError('') }, [plan])

  const readFile = async (file: File | undefined) => {
    setFileNote(''); setError('')
    if (!file) return
    if (/\.xlsx?$|\.ods$/i.test(file.name)) { setError('A workbook cannot be read here. Copy the cells and paste them, or save the sheet as CSV.'); return }
    if (file.size > MAX_FILE_BYTES) { setError('This file is larger than 2 MB. Split it into smaller files.'); return }
    try {
      setText(await file.text())
      setFileNote(file.name)
    } catch { setError('This file could not be read.') }
  }
  const separatorName = parsed?.separator === '\t' ? 'tab' : parsed?.separator === ';' ? 'semicolon' : parsed?.separator === ',' ? 'comma' : null

  return <dialog ref={modal.ref} className="mns-dialog mns-import" aria-labelledby="mns-import-title" onCancel={modal.onCancel}>
    <header><div><div className="mns-eyebrow">{sensor.name}</div><h2 id="mns-import-title">Paste or Import Readings</h2></div>
      <button type="button" className="mns-close" aria-label="Close" onClick={onClose}>×</button></header>
    <div className="mns-dialog-body">
      <p className="mns-help">Paste two or three columns from Excel or a CSV file: date and time, value, and an optional note.
        Dates like <span className="mono">2026-10-22 14:30</span>, <span className="mono">22.10.2026 14:30</span> or <span className="mono">22/10/2026 14:30</span>, day first;
        a date alone is 00:00. Times are station time, Asia/Tbilisi (UTC+4). Tabs, semicolons or commas; a header row is optional.</p>
      <label className="mns-field"><span>Readings</span>
        <textarea ref={field} className="mono" rows={8} spellCheck={false} value={text} onChange={event => { setText(event.target.value); setFileNote('') }}
          placeholder={'22.10.2026 14:30\t1,25\tAfter rain\n29.10.2026 14:30\t1,31'} /></label>
      <div className="mns-file-row">
        <label className="button mns-file"><input type="file" accept=".csv,.txt,text/csv,text/plain"
          onChange={event => { void readFile(event.target.files?.[0]); event.target.value = '' }} />Choose CSV or text file</label>
        {fileNote && <span className="mns-muted">Read from <span className="mono">{fileNote}</span></span>}
      </div>
      {error && <p className="mns-error" role="alert">{error}</p>}
      {plan && parsed && <ImportPreview sensor={sensor} plan={plan} header={parsed.header} separator={separatorName} />}
      {plan?.blocked && <p className="mns-error" role="alert">{plan.blocked}</p>}
      {plan && plan.replace > 0 && !plan.blocked && <label className="mns-check"><input type="checkbox" checked={confirmReplace}
        onChange={event => setConfirmReplace(event.target.checked)} />Replace {count(plan.replace, 'stored reading')} with the values above.</label>}
    </div>
    <footer>
      <span className="mns-muted">{SHARED_NOTE}</span>
      <div className="mns-row-actions"><button type="button" className="button" onClick={onClose}>Cancel</button>
        <button type="button" className="button primary" disabled={busy || !plan || !changes || !!plan.blocked || (plan.replace > 0 && !confirmReplace)}
          onClick={async () => {
            if (!plan) return
            setBusy(true)
            try { setError(await onApply(plan, fileNote || null) ?? '') } finally { setBusy(false) }
          }}>
          {!plan || !changes ? 'Apply' : plan.replace ? `Add ${plan.add.toLocaleString('en-GB')}, replace ${plan.replace.toLocaleString('en-GB')}` : `Add ${count(plan.add, 'reading')}`}
        </button></div>
    </footer>
  </dialog>
}

const PREVIEW_ROWS = 200

function ImportPreview({ sensor, plan, header, separator }: { sensor: ManualSensorReadings; plan: ImportPlan; header: boolean; separator: string | null }) {
  const rows = [...plan.rows].sort((a, b) => a.line - b.line)
  return <section className="mns-preview" aria-label="Import preview">
    <ul className="mns-summary">
      <li><strong className="mono">{plan.add.toLocaleString('en-GB')}</strong> new</li>
      <li><strong className="mono">{plan.replace.toLocaleString('en-GB')}</strong> replace a stored reading</li>
      <li><strong className="mono">{plan.unchanged.toLocaleString('en-GB')}</strong> already stored</li>
      <li className={plan.errors.length ? 'mns-has-errors' : ''}><strong className="mono">{plan.errors.length.toLocaleString('en-GB')}</strong> {plan.errors.length === 1 ? 'line' : 'lines'} with errors, skipped</li>
    </ul>
    <p className="mns-help">{separator ? `Read as ${separator}-separated.` : 'Read as one column.'}{header ? ' The first line was read as a header and skipped.' : ''}</p>
    {!!plan.errors.length && <div className="mns-errors" role="alert"><h3>Errors</h3><ol>
      {plan.errors.slice(0, 50).map(item => <li key={item.line + item.message}>
        <span className="mns-line">{item.line ? 'Line ' + item.line : 'Text'}</span><span>{item.message}</span>
        {item.text && <code>{item.text}</code>}</li>)}
      {plan.errors.length > 50 && <li><span>And {count(plan.errors.length - 50, 'more line')}.</span></li>}
    </ol></div>}
    {!!rows.length && <div className="mns-table-wrap mns-preview-table"><table className="mns-table">
      <thead><tr><th scope="col" className="mns-num">Line</th><th scope="col">Time (Tbilisi)</th><th scope="col" className="mns-num">Value{sensor.unit ? ` (${sensor.unit})` : ''}</th>
        <th scope="col">Note</th><th scope="col">Change</th></tr></thead>
      <tbody>{rows.slice(0, PREVIEW_ROWS).map(row => <tr key={row.line} className={'mns-row-' + row.status}>
        <td className="mns-num mono">{row.line}</td><td className="mono">{readingTimeLabel(row.ts)}</td>
        <td className="mns-num mono">{valueText(row.value, sensor.precision)}</td><td className="mns-note">{row.note ?? ''}</td>
        <td>{row.status === 'add' ? 'New' : row.status === 'unchanged' ? 'Already stored' : <>Replaces <span className="mono">{valueText(row.previous!.value, sensor.precision)}</span>{row.previous!.note ? ` (${row.previous!.note})` : ''}</>}</td>
      </tr>)}</tbody>
    </table>{rows.length > PREVIEW_ROWS && <p className="mns-help mns-inset">Showing the first {PREVIEW_ROWS} of {rows.length.toLocaleString('en-GB')} readings. Apply writes all of them.</p>}</div>}
  </section>
}

export function SensorDialog({ state, items, stations, stationsError, projects, projectsError, onClose, onSubmit }: {
  state: SensorDialogState; items: ManualSensor[]; stations: Station[]; stationsError?: string
  projects: Project[]; projectsError?: string
  onClose: () => void; onSubmit: (input: ManualSensorInput) => Promise<string | null>
}) {
  const nameField = useRef<HTMLInputElement>(null)
  const modal = useModal(onClose, nameField)
  const editing = state.mode === 'edit' ? state.sensor : null
  const [input, setInput] = useState<ManualSensorInput>(() => editing ? inputFor(editing)
    : { name: '', sensor_type: '', quantity: '', unit: '', precision: 2, station: null,
        project_id: projects.length === 1 ? projects[0]!.id : null, notes: '' })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // A new sensor's Measures box follows its name until someone types in it
  // (Tamar asked for the name to be the quantity; the box stays, so two staff
  // gauges can still share an axis, but it no longer has to be typed twice).
  const [measuresEdited, setMeasuresEdited] = useState(Boolean(editing))
  const set = <K extends keyof ManualSensorInput>(key: K, value: ManualSensorInput[K]) => { setInput(current => ({ ...current, [key]: value })); setError('') }
  // A sensor stands at one of its project's stations, or none: the server
  // refuses any other, so the list offers only those.
  const project = useAsync(() => input.project_id === null ? Promise.resolve(null) : api.projects.get(input.project_id),
    [input.project_id])
  const sorted = useMemo(() => {
    const own = new Set((project.data?.stations ?? []).map(station => station.code))
    return stations.filter(station => own.has(station.code)).sort((a, b) => a.name.localeCompare(b.name))
  }, [stations, project.data])
  const choices = useMemo(() => editing && !projects.some(item => item.id === editing.project_id)
    ? [{ id: editing.project_id, name: editing.project } as Project, ...projects] : projects, [editing, projects])
  const relabel = editing && editing.reading_count > 0 && (input.unit.trim() !== editing.unit || input.quantity.trim() !== editing.quantity)
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const invalid = validateManualSensorInput(input, items, editing?.id)
    if (invalid) { setError(invalid); return }
    setBusy(true)
    try { setError(await onSubmit(input) ?? '') } finally { setBusy(false) }
  }

  return <dialog ref={modal.ref} className="mns-dialog mns-sensor-dialog" aria-labelledby="mns-sensor-title" onCancel={modal.onCancel}>
    <form onSubmit={submit} noValidate>
      <header><div><div className="mns-eyebrow">Manual sensor</div><h2 id="mns-sensor-title">{editing ? 'Edit Properties' : 'New Manual Sensor'}</h2></div>
        <button type="button" className="mns-close" aria-label="Close" onClick={onClose}>×</button></header>
      <div className="mns-dialog-body mns-form-grid">
        <label className="mns-field mns-span"><span>Name</span>
          <input ref={nameField} required maxLength={100} value={input.name} onChange={event => {
            const name = event.target.value
            setInput(current => ({ ...current, name, ...(measuresEdited ? {} : { quantity: name.slice(0, MAX_QUANTITY) }) }))
            setError('')
          }} placeholder="e.g. Staff gauge SG-1, Lajanuri intake" /></label>
        <label className="mns-field"><span>Sensor type</span>
          <select required value={input.sensor_type} onChange={event => set('sensor_type', event.target.value as ManualSensorInput['sensor_type'])}>
            <option value="" disabled>Choose a type</option>
            {SENSOR_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
          </select></label>
        <label className="mns-field"><span>Measures</span>
          <input required maxLength={MAX_QUANTITY} list="mns-parameters" value={input.quantity} placeholder="Water level"
            onChange={event => { setMeasuresEdited(true); set('quantity', event.target.value) }} />
          <datalist id="mns-parameters">{PARAMETERS.map(item => <option key={item} value={item} />)}</datalist></label>
        <label className="mns-field"><span>Unit</span>
          <input maxLength={30} list="mns-units" value={input.unit} onChange={event => set('unit', event.target.value)} placeholder="m" />
          <datalist id="mns-units">{UNITS.map(item => <option key={item} value={item} />)}</datalist></label>
        <label className="mns-field"><span>Decimal places</span>
          <select value={input.precision} onChange={event => set('precision', Number(event.target.value))}>
            {Array.from({ length: 10 }, (_, n) => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="mns-field"><span>Project</span>
          <select required value={input.project_id ?? ''} onChange={event => {
            const id = event.target.value ? Number(event.target.value) : null
            setInput(current => ({ ...current, project_id: id, station: null })); setError('')
          }}>
            <option value="" disabled>Choose a project</option>
            {choices.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          {projectsError && <small>The project list could not be loaded.</small>}
          {!projectsError && !choices.length && <small>Manual sensors are created in a project you are an Editor or Admin of.</small>}</label>
        <label className="mns-field"><span>Station</span>
          <select value={input.station ?? ''} disabled={input.project_id === null} onChange={event => set('station', event.target.value || null)}>
            <option value="">No station</option>
            {input.station && !sorted.some(station => station.code === input.station) && <option value={input.station}>{input.station}</option>}
            {sorted.map(station => <option key={station.code} value={station.code}>{station.name}</option>)}
          </select>
          {(stationsError || project.error) && <small>The station list could not be loaded.</small>}</label>
        <label className="mns-field mns-span"><span>Notes <small>Optional</small></span>
          <textarea rows={3} maxLength={MAX_SENSOR_NOTES} value={input.notes} onChange={event => set('notes', event.target.value)}
            placeholder="Where the gauge is, its datum, how it is read" /></label>
        <p className="mns-help mns-span">The sensor gets its own chart axis for this quantity and unit, so it never shares an axis with a different quantity.
          {relabel ? ' Changing the measure or unit relabels the stored readings; it does not convert them.' : ''}</p>
        {error && <p className="mns-error mns-span" role="alert">{error}</p>}
      </div>
      <footer><span className="mns-muted">{editing ? 'Readings are kept.' : "Visible to the project's members, who can enter its readings."}</span>
        <div className="mns-row-actions"><button type="button" className="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="button primary" disabled={busy}>{editing ? 'Save properties' : 'Create sensor'}</button></div></footer>
    </form>
  </dialog>
}

function DeleteSensorDialog({ sensor, onCancel, onDelete }: { sensor: ManualSensor; onCancel: () => void; onDelete: () => Promise<string | null> }) {
  const modal = useModal(onCancel)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  return <dialog ref={modal.ref} className="mns-dialog mns-delete-dialog" aria-labelledby="mns-delete-title" onCancel={modal.onCancel}>
    <div className="mns-dialog-body">
      <h2 id="mns-delete-title">Delete Manual Sensor?</h2>
      <p><strong>{sensor.name}</strong></p>
      <p className="mns-help">{sensor.reading_count
        ? `The sensor leaves every list; its ${count(sensor.reading_count, 'reading')} ${sensor.reading_count === 1 ? 'is' : 'are'} kept on the platform, where an administrator can restore it.`
        : 'It has no readings.'} Saved analyses using it will need another source.</p>
      {error && <p className="mns-error" role="alert">{error}</p>}
      <div className="mns-row-actions mns-end"><button type="button" className="button" onClick={onCancel}>Cancel</button>
        <button type="button" className="button mns-danger-solid" disabled={busy} onClick={async () => {
          setBusy(true)
          try { setError(await onDelete() ?? '') } finally { setBusy(false) }
        }}>Delete sensor</button></div>
    </div>
  </dialog>
}
